const STORAGE_KEY = "bcm-route-scroll";
const MAX_SAVED_ROUTES = 40;

export type SavedScrollPosition = {
  windowY: number;
  containerY: number;
};

function canUseSessionStorage(): boolean {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

export function getRouteScrollKey(pathname: string, search = ""): string {
  const query = search && search !== "?" ? search : "";
  return `${pathname}${query}`;
}

export function isReturnToParentPath(
  fromPathname: string,
  toPathname: string,
): boolean {
  if (!fromPathname || !toPathname || fromPathname === toPathname) {
    return false;
  }

  // "/" is a prefix of every route; treating it as a parent would restore
  // homepage scroll whenever any public page linked home.
  if (toPathname === "/") {
    return false;
  }

  const parent = toPathname.endsWith("/") ? toPathname : `${toPathname}/`;
  return fromPathname.startsWith(parent);
}

export function getScrollableContainer(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector("[data-scroll-container]");
}

export function readCurrentScrollPosition(): SavedScrollPosition {
  const container = getScrollableContainer();
  return {
    windowY: window.scrollY || document.documentElement.scrollTop || 0,
    containerY: container?.scrollTop ?? 0,
  };
}

export function applyScrollPosition(position: SavedScrollPosition): void {
  const container = getScrollableContainer();
  if (container) {
    container.scrollTop = position.containerY;
  }
  window.scrollTo({
    top: position.windowY,
    left: 0,
    behavior: "instant",
  });
}

export function scrollRouteToTop(): void {
  applyScrollPosition({ windowY: 0, containerY: 0 });
}

function readAllPositions(): Record<string, SavedScrollPosition> {
  if (!canUseSessionStorage()) return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, SavedScrollPosition>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAllPositions(positions: Record<string, SavedScrollPosition>): void {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function saveRouteScrollPosition(
  key: string,
  position: SavedScrollPosition = readCurrentScrollPosition(),
): void {
  if (!key) return;
  const positions = readAllPositions();
  const next = { ...positions, [key]: position };
  const keys = Object.keys(next);
  if (keys.length > MAX_SAVED_ROUTES) {
    for (const extraKey of keys.slice(0, keys.length - MAX_SAVED_ROUTES)) {
      delete next[extraKey];
    }
  }
  writeAllPositions(next);
}

export function readRouteScrollPosition(
  key: string,
): SavedScrollPosition | null {
  if (!key) return null;
  return readAllPositions()[key] ?? null;
}

export function shouldRestoreRouteScroll(input: {
  popped: boolean;
  fromPathname: string;
  toPathname: string;
}): boolean {
  return (
    input.popped || isReturnToParentPath(input.fromPathname, input.toPathname)
  );
}
